import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Grid,
  Chip,
  Button,
  Link,
} from "@mui/material";
import {
  Close,
  Map,
  Mail,
  Tag,
  CalendarToday,
  Person,
} from "@mui/icons-material";
import { format } from "date-fns";

export default function ViewAllDetailsModal({ contact, open, onClose }) {
  const generateMapUrl = (address) => {
    if (!address) return null;
    if (address.latitude && address.longitude) {
      return `https://www.google.com/maps/search/?api=1&query=${address.latitude},${address.longitude}`;
    }
    const addressString = [
      address.street,
      address.city,
      address.state,
      address.zip,
      address.country,
    ]
      .filter(Boolean)
      .join(", ");
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      addressString
    )}`;
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
      maxWidth="md"
      fullWidth
      disableBackdropClick
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              bgcolor: "primary.main",
              color: "white",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
              fontSize: 18,
            }}
          >
            {contact.first_name?.[0]?.toUpperCase()}
            {contact.last_name?.[0]?.toUpperCase()}
          </Box>
          <Box>
            <Typography variant="h6">{contact.name}</Typography>
            <Box mt={0.5} display="flex" gap={1} flexWrap="wrap">
              {contact.status && (
                <Chip
                  label={contact.status_name}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
              {contact.source && (
                <Chip
                  label={contact.source_name}
                  size="small"
                  color="info"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        </Box>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ maxHeight: "75vh", overflowY: "auto" }}>
        <Grid container spacing={3}>
          {/* Left: Personal Info */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              <Person fontSize="small" sx={{ mr: 1 }} />
              Personal Information
            </Typography>
            <Box pl={1} mt={1}>
              <DetailRow label="First Name" value={contact.first_name} />
              <DetailRow label="Last Name" value={contact.last_name} />
              <DetailRow label="Company" value={contact.company} />
              <DetailRow label="Position" value={contact.position} />
              <DetailRow label="Source" value={contact.source_name} />
              <DetailRow label="Status" value={contact.status_name} />
            </Box>
          </Grid>

          {/* Right: Contact Info */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              <Mail fontSize="small" sx={{ mr: 1 }} />
              Contact Information
            </Typography>

            {/* Emails */}
            {contact.emails?.map((email, i) => (
              <Box key={i} p={1} bgcolor="#f9f9f9" borderRadius={1} mt={1}>
                <Link
                  href={`mailto:${email.email}`}
                  underline="hover"
                  color="primary"
                >
                  {email.email}
                </Link>
                <Box mt={0.5} display="flex" gap={1} flexWrap="wrap">
                  <Chip label={email.type} size="small" variant="outlined" />
                  {email.primary && (
                    <Chip label="Primary" size="small" color="success" />
                  )}
                </Box>
              </Box>
            ))}

            {/* Phones */}
            {contact.phone_numbers?.map((phone, i) => (
              <Box key={i} p={1} bgcolor="#f9f9f9" borderRadius={1} mt={2}>
                <Link
                  href={`tel:${phone.number}`}
                  underline="hover"
                  color="primary"
                >
                  {phone.number}
                </Link>
                {phone.name && (
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                  >
                    {phone.reference}
                  </Typography>
                )}
                <Box mt={0.5} display="flex" gap={1} flexWrap="wrap">
                  <Chip label={phone.type} size="small" variant="outlined" />
                  {phone.primary && (
                    <Chip label="Primary" size="small" color="success" />
                  )}
                </Box>
              </Box>
            ))}
          </Grid>

          {/* Addresses */}
          {contact.addresses?.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                <Map fontSize="small" sx={{ mr: 1 }} />
                Addresses
              </Typography>
              <Grid container spacing={2}>
                {contact.addresses.map((addr, i) => (
                  <Grid item xs={12} md={6} key={i}>
                    <Box p={2} bgcolor="#f5f5f5" borderRadius={1}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="start"
                      >
                        <Box>
                          <Box display="flex" gap={1} mb={1}>
                            <Chip
                              label={addr.type}
                              size="small"
                              variant="outlined"
                            />
                            {addr.primary && (
                              <Chip
                                label="Primary"
                                size="small"
                                color="success"
                              />
                            )}
                          </Box>
                          {addr.street && (
                            <Typography variant="body2">
                              {addr.street}
                            </Typography>
                          )}
                          <Typography variant="body2">
                            {[addr.city, addr.state, addr.zip]
                              .filter(Boolean)
                              .join(", ")}
                          </Typography>
                          {addr.country && (
                            <Typography variant="body2">
                              {addr.country}
                            </Typography>
                          )}
                        </Box>
                        {generateMapUrl(addr) && (
                          <IconButton
                            href={generateMapUrl(addr)}
                            target="_blank"
                            size="small"
                            title="View on Map"
                          >
                            <Map fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}

          {/* Notes */}
          {contact.notes && (
            <Grid item xs={12}>
              <Typography variant="subtitle1">Notes</Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: "pre-wrap", mt: 1 }}
              >
                {contact.notes}
              </Typography>
            </Grid>
          )}

          {/* Tags */}
          {contact.tags?.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle1">
                <Tag fontSize="small" sx={{ mr: 1 }} />
                Tags
              </Typography>
              <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
                {contact.tags.map((tag, i) => (
                  <Chip key={i} label={tag} size="small" variant="outlined" />
                ))}
              </Box>
            </Grid>
          )}

          {/* Metadata */}
          <Grid item xs={12}>
            <Box
              mt={2}
              pt={2}
              borderTop="1px solid #eee"
              display="flex"
              gap={4}
              flexWrap="wrap"
            >
              <Typography
                variant="caption"
                color="text.secondary"
                display="flex"
                alignItems="center"
                gap={1}
              >
                <CalendarToday fontSize="inherit" />
                Created:{" "}
                {format(new Date(contact.createdat), "MMM d, yyyy h:mm a")}
              </Typography>
              {contact.modifiedat !== contact.createdat && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <CalendarToday fontSize="inherit" />
                  Updated:{" "}
                  {format(new Date(contact.modifiedat), "MMM d, yyyy h:mm a")}
                </Typography>
              )}
              {contact.assigned_to && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Person fontSize="inherit" />
                  Assigned To: {contact.username}
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const DetailRow = ({ label, value }) => (
  <Box display="flex" mb={1}>
    <Typography fontWeight={500} sx={{ minWidth: 100 }}>
      {label}:
    </Typography>
    <Typography color="text.secondary">{value || "—"}</Typography>
  </Box>
);
